import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  Spinner,
  Stack,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  FiArrowLeft,
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiXCircle,
} from "react-icons/fi";
import { Link as RouterLink, useParams } from "react-router-dom";
import { InvestmentPaymentCard } from "../components/common/InvestmentPaymentCard";
import { ROUTE_PATHS } from "../config/routes";
import {
  easyPurchase,
  requests,
  PaymentResultRequestResponse,
} from "../services/api";

const statusLabel: Record<string, string> = {
  paid: "پرداخت موفق",
  failed: "پرداخت ناموفق",
  cancelled: "پرداخت لغو شد",
  token_received: "در انتظار تایید",
  initiated: "در انتظار پرداخت",
  wait_for_payment: "در انتظار پرداخت",
  reviewing: "در حال بررسی",
  approved: "تایید شده",
  rejected: "رد شده",
  not_found: "رسید یافت نشد",
};

const externalTerminalStatuses = new Set(["failed"]);

const allowedStatuses = new Set([
  "paid",
  "failed",
  "cancelled",
  "token_received",
  "initiated",
  "wait_for_payment",
  "reviewing",
  "approved",
  "rejected",
  "not_found",
]);

const paymentMethodLabel: Record<string, string> = {
  gateway: "درگاه پرداخت",
  card: "کارت هدیه",
  slip: "فیش انتقال",
};

const shouldConfirmSepahPayment = (
  requestResult: PaymentResultRequestResponse | null,
) =>
  requestResult?.externalSource === "sepah_broker" &&
  requestResult?.paymentMethod === "gateway" &&
  requestResult?.operationType === "issuance" &&
  requestResult?.status === "wait_for_payment";

export const PaymentResultPage: React.FC = () => {
  const { requestId = "" } = useParams<{ requestId: string }>();
  const normalizedRequestId = normalizeRequestId(requestId);
  const [requestResult, setRequestResult] =
    useState<PaymentResultRequestResponse | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(normalizedRequestId));
  const [errorMessage, setErrorMessage] = useState("");

  const pageBg = useColorModeValue("light.900", "dark.900");
  const panelBg = useColorModeValue("white", "dark.50");
  const borderColor = useColorModeValue("blackAlpha.100", "whiteAlpha.200");
  const heroGlow = useColorModeValue(
    "radial-gradient(circle at top right, rgba(173,11,6,0.18), transparent 42%), radial-gradient(circle at bottom left, rgba(244,114,101,0.16), transparent 38%)",
    "radial-gradient(circle at top right, rgba(255,255,255,0.08), transparent 40%), radial-gradient(circle at bottom left, rgba(173,11,6,0.18), transparent 38%)",
  );

  useEffect(() => {
    if (!normalizedRequestId) {
      setIsLoading(false);
      setErrorMessage("لینک نتیجه پرداخت معتبر نیست.");
      return;
    }

    let isMounted = true;

    const loadRequestResult = async () => {
      try {
        setIsLoading(true);
        setErrorMessage("");
        let response = await requests.getOne(normalizedRequestId);

        if (shouldConfirmSepahPayment(response)) {
          const confirmation =
            await easyPurchase.confirmSepahPayment(normalizedRequestId);
          response = confirmation.data || response;
        }

        if (isMounted) {
          setRequestResult(response);
        }
      } catch (error: any) {
        if (isMounted) {
          setErrorMessage(
            error.response?.data?.message ||
              "امکان دریافت نتیجه پرداخت وجود ندارد.",
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadRequestResult();

    return () => {
      isMounted = false;
    };
  }, [normalizedRequestId]);

  const resolvedPaymentMethod = requestResult?.paymentMethod || "";
  const displayStatus = resolveDisplayStatus(requestResult);
  const isSuccess = displayStatus === "approved";
  const isCancelled = displayStatus === "cancelled";
  const isSlipMethod = resolvedPaymentMethod === "slip";
  const isRedemption = requestResult?.operationType === "redemption";
  const sepahOperationLabel = isRedemption ? "فروش" : "خرید";
  const shouldShowSepahProcessingNotice =
    requestResult?.externalSource === "sepah_broker" &&
    requestResult?.status === "reviewing";
  const displayFundName = requestResult?.fundName || "";
  const displayAmount =
    requestResult?.amountRial ||
    requestResult?.paymentSlip?.amountRial ||
    requestResult?.paymentCard?.amount ||
    0;
  const displayOrderId =
    requestResult?.transaction?.orderId ||
    requestResult?.paymentGateway?.orderId ||
    "";
  const displayTrackId = requestResult?.trackId || "";
  const providerLabel =
    requestResult?.externalSource === "sepah_broker" ? "کارگزاری سپه" : "تدبیر";
  const displayCardMask =
    requestResult?.transaction?.cardMask ||
    requestResult?.paymentGateway?.cardMask ||
    "";
  const displayRrn =
    requestResult?.transaction?.rrn || requestResult?.paymentGateway?.rrn || "";
  const displayTime =
    requestResult?.transaction?.updatedAt ||
    requestResult?.paymentSlip?.transferedAt ||
    requestResult?.externalLastSyncedAt ||
    requestResult?.updatedAt ||
    requestResult?.createdAt;
  const statusMeta = useMemo(() => {
    if (!normalizedRequestId) {
      return {
        icon: FiAlertCircle,
        title: "شناسه درخواست در دسترس نیست",
        message:
          "برای نمایش نتیجه، شناسه درخواست باید در لینک وجود داشته باشد.",
        gradient: "linear(135deg, #ea580c 0%, #f97316 55%, #fed7aa 100%)",
      };
    }

    if (isSlipMethod) {
      if (isSuccess) {
        return {
          icon: FiCheckCircle,
          title: "درخواست صدور با موفقیت پردازش شد",
          message: `فیش انتقال شما بررسی شد و درخواست صدور در ${providerLabel} با موفقیت پردازش شد.`,
          gradient: "linear(135deg, #0f9d58 0%, #38b26c 55%, #d9f7e7 100%)",
        };
      }

      if (displayStatus === "rejected" || displayStatus === "failed") {
        return {
          icon: FiXCircle,
          title: "درخواست صدور ناموفق بود",
          message: `فیش انتقال ثبت شد، اما درخواست صدور در ${providerLabel} رد یا ناموفق شده است.`,
          gradient: "linear(135deg, #ad0b06 0%, #d43c35 55%, #f6c1bc 100%)",
        };
      }

      return {
        icon: FiClock,
        title: "فیش انتقال شما ثبت شد",
        message: "درخواست شما در اولین روز کاری بررسی خواهد شد",
        gradient: "linear(135deg, #77aef1 0%, #3b82f6 55%, #2563eb 100%)",
      };
    }

    if (isSuccess) {
      return {
        icon: FiCheckCircle,
        title: isRedemption
          ? "درخواست ابطال با موفقیت پردازش شد"
          : "درخواست صدور با موفقیت پردازش شد",
        message: isRedemption
          ? `درخواست ابطال شما در ${providerLabel} با موفقیت نهایی شد.`
          : `پرداخت انجام شد و درخواست صدور شما در ${providerLabel} با موفقیت نهایی شد.`,
        gradient: "linear(135deg, #74e8aa 0%, #38b26c 55%, #0f9d58 100%)",
      };
    }

    if (
      displayStatus === "reviewing" ||
      displayStatus === "wait_for_payment" ||
      displayStatus === "paid"
    ) {
      return {
        icon: FiClock,
        title: isRedemption
          ? "درخواست ابطال در حال بررسی است"
          : "پرداخت با موفقیت انجام شد",
        message: "درخواست شما در اولین روز کاری بررسی خواهد شد",
        gradient: "linear(135deg, #77aef1 0%, #3b82f6 55%, #2563eb 100%)",
      };
    }

    if (isCancelled) {
      return {
        icon: FiXCircle,
        title: "پرداخت لغو شد",
        message: "پرداخت توسط کاربر لغو شده و درخواستی ثبت نشده است.",
        gradient:
          "linear-gradient(135deg, #6f0805 0%, #ad0b06 48%, #d43c35 100%)",
      };
    }

    return {
      icon: FiXCircle,
      title: isRedemption
        ? "درخواست ابطال ناموفق بود"
        : "درخواست صدور ناموفق بود",
      message:
        displayStatus === "failed"
          ? isRedemption
            ? `درخواست ابطال در ${providerLabel} رد یا ناموفق شده است.`
            : `اگر مبلغی از حساب شما کسر شده باشد، پرداخت انجام شده اما درخواست صدور در ${providerLabel} ناموفق بوده است.`
          : "اگر مبلغی از حساب شما کسر شده باشد، طبق قواعد شبکه بانکی برگشت داده می‌شود.",
      gradient: "linear(135deg, #ad0b06 0%, #d43c35 55%, #f6c1bc 100%)",
    };
  }, [
    displayStatus,
    isCancelled,
    isSlipMethod,
    isSuccess,
    isRedemption,
    normalizedRequestId,
    providerLabel,
  ]);

  const isPendingLoad = isLoading && !requestResult && !errorMessage;
  const shouldShowPaymentCard =
    displayStatus !== "failed" && resolvedPaymentMethod === "card";

  if (isPendingLoad) {
    return (
      <VStack spacing={5} h="100dvh" justify="center" textAlign="center">
        <Spinner
          thickness="4px"
          speed="0.8s"
          emptyColor="gray.200"
          color="brand.500"
          size="xl"
        />
        <VStack spacing={2}>
          <Heading size="md">در حال دریافت نتیجه پرداخت</Heading>
          <Text color="gray.500" maxW="420px">
            چند لحظه صبر کنید تا اطلاعات درخواست و وضعیت پرداخت بارگذاری شود.
          </Text>
        </VStack>
      </VStack>
    );
  }

  return (
    <Box minH="100vh" bg={pageBg} py={{ base: 8, md: 12 }} px={4} dir="rtl">
      <Container maxW="920px">
        <Box
          bg={panelBg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="3xl"
          p={{ base: 5, md: 8 }}
          position="relative"
          overflow="hidden"
        >
          <Box
            position="absolute"
            inset={0}
            bgImage={heroGlow}
            pointerEvents="none"
          />

          <Stack spacing={6} position="relative" zIndex={1}>
            <Box
              borderRadius="3xl"
              px={{ base: 5, md: 7 }}
              py={{ base: 6, md: 8 }}
              bgGradient={statusMeta.gradient}
              color="white"
            >
              <HStack justify="space-between" align="flex-start" spacing={6}>
                <VStack align="flex-start" spacing={3} maxW="620px">
                  <Heading size={{ base: "md", md: "lg" }}>
                    {statusMeta.title}
                  </Heading>
                  <Text color="whiteAlpha.900" lineHeight="tall">
                    {statusMeta.message}
                  </Text>
                </VStack>
              </HStack>
            </Box>

            {shouldShowPaymentCard && (
              <InvestmentPaymentCard
                cardNumber={displayCardMask}
                amount={displayAmount}
                expiresInfo={{
                  label: "تاریخ",
                  value: requestResult?.paymentCard?.expiresAt || displayTime,
                }}
                status={requestResult?.paymentCard?.status || displayStatus}
                usedAt={requestResult?.paymentCard?.usedAt}
                fundName={displayFundName || "صندوق سرمایه‌گذاری"}
              />
            )}

            {errorMessage && (
              <Box
                bg="red.50"
                _dark={{ bg: "rgba(255,0,0,0.08)" }}
                borderRadius="2xl"
                p={4}
              >
                <Text color="red.500">{errorMessage}</Text>
              </Box>
            )}

            {shouldShowSepahProcessingNotice && (
              <Box
                bg="blue.50"
                _dark={{ bg: "whiteAlpha.100" }}
                borderRadius="2xl"
                p={4}
              >
                <VStack align="stretch" spacing={3}>
                  <Text fontWeight="bold">
                    ثبت درخواست {sepahOperationLabel}
                  </Text>
                  <Text color="gray.600" _dark={{ color: "gray.300" }}>
                    عملیات ثبت درخواست {sepahOperationLabel} شما حداکثر تا پنج
                    دقیقه آینده به‌صورت سیستمی انجام خواهد شد. لطفاً توجه داشته
                    باشید که تأمین سرمایه امید هیچ‌گونه تعهدی نسبت به قیمت نهایی{" "}
                    {sepahOperationLabel} ندارد. قیمت نهایی در روز کاری بعد توسط
                    کارگزاری تعیین شده و درخواست {sepahOperationLabel} بر اساس
                    همان قیمت ثبت خواهد شد.
                  </Text>
                </VStack>
              </Box>
            )}

            <InfoPanel
              title="جزئیات تراکنش"
              icon={FiFileText}
              rows={[
                {
                  label: "صندوق",
                  value: displayFundName || "-",
                },
                {
                  label: "وضعیت",
                  value: statusLabel[displayStatus] || displayStatus || "-",
                },
                {
                  label: "روش پرداخت",
                  value: paymentMethodLabel[resolvedPaymentMethod] || "-",
                },
                { label: "مبلغ", value: formatRial(displayAmount) },
                {
                  label: isSlipMethod ? "شماره رهگیری" : "شماره سفارش",
                  value: isSlipMethod
                    ? displayTrackId || "-"
                    : displayOrderId || displayTrackId || "-",
                },
                {
                  label: "زمان",
                  value: formatDate(displayTime),
                },
                { label: "شماره مرجع بانکی", value: displayRrn || "-" },
                {
                  label: "کارت",
                  value: isSlipMethod ? (
                    "-"
                  ) : (
                    <Text
                      as="span"
                      dir="ltr"
                      style={{ unicodeBidi: "plaintext" }}
                    >
                      {formatCardLike(displayCardMask)}
                    </Text>
                  ),
                },
              ]}
            />

            <HStack
              spacing={3}
              justify="center"
              flexWrap={{ base: "wrap", md: "nowrap" }}
            >
              <Button
                as={RouterLink}
                to={ROUTE_PATHS.HOME}
                leftIcon={<Icon as={FiArrowLeft as any} />}
                flex={{ base: "1 1 100%", md: "0 1 240px" }}
                borderRadius="full"
                variant="outline"
              >
                بازگشت به صفحه اصلی
              </Button>
            </HStack>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

const normalizeRequestId = (value?: string) =>
  String(value || "")
    .trim()
    .slice(0, 64);

const resolveDisplayStatus = (
  requestResult: PaymentResultRequestResponse | null,
) => {
  const externalStatus = String(requestResult?.externalStatus || "").trim();
  const localStatus = String(requestResult?.status || "").trim();

  if (externalStatus && externalTerminalStatuses.has(localStatus)) {
    return localStatus;
  }

  const transactionStatus = String(
    requestResult?.transaction?.status || "",
  ).trim();
  if (allowedStatuses.has(transactionStatus)) {
    return transactionStatus;
  }

  const requestStatus = String(requestResult?.status || "").trim();
  if (allowedStatuses.has(requestStatus)) {
    return requestStatus;
  }

  return requestResult?.paymentMethod === "slip" ? "reviewing" : "";
};

const formatRial = (amount?: number) => {
  if (!amount) {
    return "-";
  }

  return `${amount.toLocaleString("fa-IR")} ریال`;
};

const formatDate = (value?: string) => {
  if (!value) {
    return "-";
  }

  return new Intl.DateTimeFormat("fa-IR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
};

const formatCardLike = (value?: string | null) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "-";
  return normalized.replace(/(.{4})(?=.)/g, "$1-");
};

const InfoPanel = ({
  title,
  icon,
  rows,
}: {
  title: string;
  icon: any;
  rows: Array<{ label: string; value: React.ReactNode }>;
}) => {
  const iconBg = useColorModeValue("brand.100", "icon.bg");
  const iconColor = useColorModeValue("brand.500", "icon.svg");

  return (
    <Box
      borderWidth="1px"
      borderColor="blackAlpha.100"
      _dark={{ borderColor: "whiteAlpha.200" }}
      borderRadius="3xl"
      overflow="hidden"
    >
      <HStack
        px={5}
        py={4}
        spacing={3}
        bg="white"
        _dark={{ bg: "whiteAlpha.100" }}
      >
        <Box p={3} borderRadius="xl" bg={iconBg} color={iconColor}>
          <Icon as={icon} boxSize={5} verticalAlign={"middle"} />
        </Box>
        <Text fontWeight="bold">{title}</Text>
      </HStack>
      <Divider />
      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4} p={5}>
        {rows.map((row) => (
          <GridItem key={`${title}-${row.label}`}>
            <Text fontSize="xs" color="gray.500" mb={1.5}>
              {row.label}
            </Text>
            <Text fontWeight="semibold">{row.value || "-"}</Text>
          </GridItem>
        ))}
      </Grid>
    </Box>
  );
};
